import React, { useEffect, useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, Receipt as ReceiptIcon, Landmark } from "lucide-react";

const TYPE_META = {
  receipt: { label: "Beleg/Quittung", icon: ReceiptIcon, route: "/receipts" },
  invoice: { label: "Kundenrechnung", icon: FileText, route: "/invoices" },
  bill: { label: "Eingangsrechnung", icon: FileText, route: "/bills" },
  direct: { label: "Direkt verbucht", icon: Landmark, route: null },
  payout: { label: "Sammel-Auszahlung", icon: Landmark, route: null },
};

const fmt = (v) => (v == null ? "—" : Number(v).toFixed(2));

export const BankMatchDetailCard = ({ companyId, entryId }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entryId) return;
    setDetail(null);
    setLoading(true);
    apiClient
      .get(`/companies/${companyId}/bank-transactions/${entryId}/match-detail`)
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [companyId, entryId]);

  if (loading) return <div className="text-xs text-slate-400 py-4">Lade Zuordnungs-Details…</div>;
  if (!detail) return null;
  const m = detail.matched;
  const meta = m ? TYPE_META[m.type] || {} : {};
  const Icon = meta.icon || FileText;

  return (
    <div className="text-left max-w-xl mx-auto space-y-3" data-testid="bank-match-detail">
      {m ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
          <div className="text-[11px] text-emerald-800 uppercase tracking-wider mb-1.5">Zugeordnet zu</div>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0">
              <Icon className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {m.type === "receipt" && (m.description || m.file_name)}
                  {m.type === "invoice" && `${m.customer_name || ""} · ${m.invoice_number || ""}`}
                  {m.type === "bill" && `${m.supplier_name || ""} · ${m.bill_number || ""}`}
                  {m.type === "direct" && `Konto ${m.account_id}${m.account_name ? ` — ${m.account_name}` : ""}`}
                  {m.type === "payout" && `Sammel-Auszahlung ${m.provider} — ${m.count} App-Zahlung(en)`}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 space-x-2">
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5">{meta.label}</Badge>
                  {m.amount != null && <span>{fmt(m.amount)} {m.currency || "CHF"}</span>}
                  {m.date && <span>· {formatDate(m.date)}</span>}
                  {m.matched_by === "external_app" && <span className="text-emerald-700">· 🟢 via App verbucht</span>}
                  {m.file_name && <span className="text-slate-400">· {m.file_name}</span>}
                </div>
              </div>
            </div>
            {meta.route && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => navigate(`${meta.route}?highlight=${m.id}`)}
                data-testid="bank-match-detail-open"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Öffnen
              </Button>
            )}
          </div>
          {m.type === "payout" && (m.payments || []).length > 0 && (
            <ul className="mt-2 space-y-0.5 text-xs text-slate-600 border-t border-emerald-200 pt-2">
              {m.payments.map((p, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="truncate">{p.customer_name} · {p.invoice_number}</span>
                  <span className="tabular-nums shrink-0">{fmt(p.amount)} CHF</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="text-xs text-slate-500">Kein verknüpftes Dokument gefunden.</div>
      )}

      {(detail.booking_lines || []).length > 0 && (
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mb-1.5">So wurde gebucht</div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="font-normal pb-1">Konto</th>
                <th className="font-normal pb-1 text-right">Soll</th>
                <th className="font-normal pb-1 text-right">Haben</th>
              </tr>
            </thead>
            <tbody>
              {detail.booking_lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1">{l.account_id}{l.account_name ? ` — ${l.account_name}` : ""}</td>
                  <td className="py-1 text-right tabular-nums">{l.debit ? fmt(l.debit) : ""}</td>
                  <td className="py-1 text-right tabular-nums">{l.credit ? fmt(l.credit) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
